import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarDays, MapPin, Phone, Users } from "lucide-react";
import { useTours, tourCountWord } from "../hooks/useTours";
import "./BookATour.css";

const steps = [
  { title: "Send your request", copy: "Share your city, venue, preferred date, and audience size." },
  { title: "We check the routing", copy: "Our team reviews the tour map and confirms what is possible around it." },
  { title: "We lock the date", copy: "You receive availability, production requirements, and pricing to confirm." },
];

export default function BookATour() {
  const { tours } = useTours();

  // Tour phone numbers are managed per tour in admin/tours.php; show each once.
  const phones = [...new Set(tours.flatMap((tour) => tour.phones ?? []))];

  useEffect(() => {
    document.title = "Book a Tour | 3E Entertainment";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Choose a 3E Entertainment USA tour — Dashain Tihar Dhamaka 2026 or Mukti & Revival — and request a date for your city."
      );
  }, []);

  return (
    <div className="choose-tour">
      <section className="choose-hero">
        <div className="choose-glow choose-glow-one" />
        <div className="choose-glow choose-glow-two" />
        <motion.div
          className="choose-hero-copy"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: .8, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1>{tourCountWord(tours.length)} {tours.length === 1 ? "tour" : "tours"}. <em>Pick your night.</em></h1>
          <p>Every production below is routing cities across the USA right now. Choose the show that fits your audience and send us your date — our tour team takes it from there.</p>
          <div className="choose-hero-meta">
            <span><Users size={16} /> Concerts · Festivals · Private events</span>
            <span><MapPin size={16} /> Cities across the USA</span>
          </div>
        </motion.div>
      </section>

      <section className="choose-grid" aria-label="Available tours">
        {tours.map((tour, index) => (
          <motion.article
            key={tour.slug}
            className="choose-card"
            data-tour-theme={tour.theme}
            initial={{ opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: .25 }}
            transition={{ delay: index * .1, duration: .75, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link className="choose-card-link" to={`/book-a-tour/${tour.slug}`} aria-label={`Book the ${tour.name} ${tour.edition}`}>
              <div className="choose-card-poster">
                <img src={tour.poster} alt={tour.posterAlt} loading={index === 0 ? "eager" : "lazy"} />
                <span className="choose-card-badge">Now booking</span>
              </div>

              <div className="choose-card-body">
                <span className="choose-card-eyebrow">{tour.edition}</span>
                <h2>{tour.name}</h2>
                <p className="choose-card-lineup">{tour.lineup}</p>
                <p className="choose-card-blurb">{tour.cardBlurb}</p>

                <dl className="choose-card-facts">
                  <div><dt><CalendarDays size={15} /> When</dt><dd>{tour.windowLabel}</dd></div>
                  <div><dt><MapPin size={15} /> Where</dt><dd>{tour.regionLabel}</dd></div>
                </dl>

                <span className="choose-card-cta">
                  Book this tour <span className="choose-card-arrow"><ArrowUpRight size={18} /></span>
                </span>
              </div>
            </Link>
          </motion.article>
        ))}
      </section>

      <section className="choose-steps" aria-labelledby="choose-steps-heading">
        <div className="choose-steps-heading">
          <h2 id="choose-steps-heading">Three steps to your show.</h2>
        </div>
        <ol className="choose-steps-list">
          {steps.map((step, index) => (
            <li key={step.title}>
              <span className="choose-step-number">{String(index + 1).padStart(2, "0")}</span>
              <strong>{step.title}</strong>
              <p>{step.copy}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="choose-contact">
        <div>
          <h2>Talk to the tour team.</h2>
          <p>We will help you match the right show to your audience, venue, and budget.</p>
        </div>
        <div className="choose-contact-lines">
          {phones.map((phone) => (
            <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`}><Phone size={17} /> {phone}</a>
          ))}
        </div>
      </section>
    </div>
  );
}
